import { useCallback, useEffect, useMemo, useState } from "react";
import client from "libs/client";
import { discoverGamesBackendAdapter } from "../../../../datagrid-configs/discoverGames/backend";
import type { FilterValues } from "../../../../datagrid-configs/types";
import { buildDynamicFilterOptions } from "../helpers/buildDynamicFilterOptions";

export interface UseDiscoverGamesFiltersArgs {
	storageKey: string;
}

export function useDiscoverGamesFilters(args: UseDiscoverGamesFiltersArgs) {
	const { storageKey } = args;
	const filtersCollapsedKey = `${storageKey}_filtersCollapsed`;

	const [filterValues, setFilterValues] = useState<FilterValues>({});
	const [filtersCollapsed, setFiltersCollapsed] = useState(() => {
		const stored = localStorage.getItem(filtersCollapsedKey);
		return stored === "true";
	});

	const { data: ownerFilterOptions } = client.getDiscoverGamesV2OwnerFilterOptions.useQuery({});
	const { data: countries } = client.getDiscoverCountries.useQuery({});
	const { data: clubManagerFilterOptions } = client.getDiscoverGamesV2FilterOptions.useQuery({
		filter: "clubManager",
	});
	const { data: contentManagerFilterOptions } = client.getDiscoverGamesV2FilterOptions.useQuery({
		filter: "contentManager",
	});
	const { data: assigneeFilterOptions } = client.getDiscoverGamesV2FilterOptions.useQuery({
		filter: "assignee",
	});
	const { data: gameStatusFilterOptions } = client.getDiscoverGamesV2GameStatusOptions.useQuery({});

	const dynamicOptions = useMemo(
		() =>
			buildDynamicFilterOptions({
				ownerFilterOptions: ownerFilterOptions?.data,
				countries,
				clubManagerFilterOptions: clubManagerFilterOptions?.data,
				contentManagerFilterOptions: contentManagerFilterOptions?.data,
				assigneeFilterOptions: assigneeFilterOptions?.data,
				gameStatusFilterOptions: gameStatusFilterOptions?.data,
			}),
		[
			ownerFilterOptions,
			countries,
			clubManagerFilterOptions,
			contentManagerFilterOptions,
			assigneeFilterOptions,
			gameStatusFilterOptions,
		],
	);

	const apiFilters = useMemo(() => {
		return discoverGamesBackendAdapter.buildFilters(filterValues);
	}, [filterValues]);

	const handleFilterChange = useCallback((values: FilterValues) => {
		setFilterValues(values);
	}, []);

	const handleClearFilters = useCallback(() => {
		setFilterValues({});
	}, []);

	const handleToggleFiltersCollapse = useCallback(() => {
		setFiltersCollapsed((prev) => !prev);
	}, []);

	useEffect(() => {
		localStorage.setItem(filtersCollapsedKey, String(filtersCollapsed));
	}, [filtersCollapsed, filtersCollapsedKey]);

	return {
		filterValues,
		filtersCollapsed,
		dynamicOptions,
		apiFilters,
		handleFilterChange,
		handleClearFilters,
		handleToggleFiltersCollapse,
	};
}
